import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import * as api from '../utils/api';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Modal = styled.div`
  background: ${props => props.$darkMode ? '#0d1117' : '#ffffff'};
  border-radius: 8px;
  width: 90%;
  max-width: 700px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0,0,0,0.3);
`;

const Header = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${props => props.$darkMode ? '#30363d' : '#e1e4e8'};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${props => props.$darkMode ? '#8b949e' : '#586069'};
  padding: 0;
  width: 32px;
  height: 32px;
  
  &:hover {
    color: ${props => props.$darkMode ? '#c9d1d9' : '#24292f'};
  }
`;

const Content = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RuleItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  margin: 6px 0;
  background: ${props => props.$darkMode ? '#161b22' : '#f6f8fa'};
  border: 1px solid ${props => props.$darkMode ? '#30363d' : '#e1e4e8'};
  border-radius: 6px;
  
  &:hover {
    background: ${props => props.$darkMode ? '#21262d' : '#eaeef2'};
  }
`;

const RuleInfo = styled.div`
  flex: 1;
`;

const RuleName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
`;

const RuleDescription = styled.div`
  font-size: 11px;
  color: ${props => props.$darkMode ? '#8b949e' : '#586069'};
  margin-top: 2px;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
    transition: 0.3s;
    border-radius: 22px;
    
    &:before {
      position: absolute;
      content: "";
      height: 16px;
      width: 16px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
    }
  }
  
  input:checked + span {
    background-color: #2da44e;
  }
  
  input:checked + span:before {
    transform: translateX(18px);
  }
`;

const Footer = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${props => props.$darkMode ? '#30363d' : '#e1e4e8'};
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  background: ${props => props.$primary ? '#2da44e' : (props.$darkMode ? '#21262d' : '#ffffff')};
  color: ${props => props.$primary ? '#ffffff' : (props.$darkMode ? '#e6edf3' : '#24292f')};
  
  &:hover {
    background: ${props => props.$primary ? '#2c974b' : (props.$darkMode ? '#30363d' : '#f6f8fa')};
  }
`;

const IBM_RULES = [
  { code: 'ibm-api-symmetry', name: 'API Symmetry', description: 'API operations should be symmetric' },
  { code: 'ibm-array-attributes', name: 'Array Attributes', description: 'Array schemas should have proper attributes' },
  { code: 'ibm-avoid-inline-schemas', name: 'Avoid Inline Schemas', description: 'Avoid inline schemas, use $ref instead' },
  { code: 'ibm-avoid-property-name-collision', name: 'No Property Collision', description: 'Avoid property name collisions' },
  { code: 'ibm-collection-array-property', name: 'Collection Array', description: 'Collection responses should return arrays' },
  { code: 'ibm-content-contains-schema', name: 'Content Has Schema', description: 'Content should contain schema' },
  { code: 'ibm-discriminator-property', name: 'Discriminator Property', description: 'Discriminator must reference existing property' },
  { code: 'ibm-enum-casing-convention', name: 'Enum Casing', description: 'Enum values should follow casing convention' },
  { code: 'ibm-error-content-type-is-json', name: 'Error Content Type', description: 'Error responses should be JSON' },
  { code: 'ibm-error-response-schemas', name: 'Error Response Schema', description: 'Error responses should have proper schema' },
  { code: 'ibm-major-version-in-path', name: 'Version in Path', description: 'Major version should be in path' },
  { code: 'ibm-no-accept-header', name: 'No Accept Header', description: 'Do not define Accept header parameter' },
  { code: 'ibm-no-authorization-header', name: 'No Auth Header', description: 'Do not define Authorization header parameter' },
  { code: 'ibm-no-content-type-header', name: 'No Content-Type Header', description: 'Do not define Content-Type header parameter' },
  { code: 'ibm-no-crn-path-parameters', name: 'No CRN in Path', description: 'Avoid CRN in path parameters' },
  { code: 'ibm-no-duplicate-description', name: 'No Duplicate Description', description: 'Avoid duplicate descriptions' },
  { code: 'ibm-no-if-modified-since-header', name: 'No If-Modified-Since', description: 'Do not define If-Modified-Since header' },
  { code: 'ibm-no-if-unmodified-since-header', name: 'No If-Unmodified-Since', description: 'Do not define If-Unmodified-Since header' },
  { code: 'ibm-operationid-casing-convention', name: 'OperationId Casing', description: 'OperationId should follow casing convention' },
  { code: 'ibm-operationid-naming-convention', name: 'OperationId Naming', description: 'OperationId should follow naming convention' },
  { code: 'ibm-pagination-style', name: 'Pagination Style', description: 'Pagination should follow standard style' },
  { code: 'ibm-parameter-casing-convention', name: 'Parameter Casing', description: 'Parameters should follow casing convention' },
  { code: 'ibm-parameter-description', name: 'Parameter Description', description: 'Parameters should have descriptions' },
  { code: 'ibm-parameter-order', name: 'Parameter Order', description: 'Parameters should be in correct order' },
  { code: 'ibm-patch-request-content-type', name: 'Patch Content Type', description: 'PATCH should use merge-patch+json' },
  { code: 'ibm-path-segment-casing-convention', name: 'Path Segment Casing', description: 'Path segments should follow casing convention' },
  { code: 'ibm-property-casing-convention', name: 'Property Casing', description: 'Properties should follow casing convention' },
  { code: 'ibm-property-description', name: 'Property Description', description: 'Properties should have descriptions' },
  { code: 'ibm-request-and-response-content', name: 'Request/Response Content', description: 'Requests and responses should have content' },
  { code: 'ibm-schema-casing-convention', name: 'Schema Casing', description: 'Schema names should follow casing convention' },
  { code: 'ibm-schema-description', name: 'Schema Description', description: 'Schemas should have descriptions' },
  { code: 'ibm-sdk-operations', name: 'SDK Operations', description: 'Operations should be SDK-friendly' },
  { code: 'ibm-string-attributes', name: 'String Attributes', description: 'String schemas should have proper attributes' },
  { code: 'ibm-success-response-example', name: 'Success Example', description: 'Success responses should have examples' },
  { code: 'ibm-valid-path-segments', name: 'Valid Path Segments', description: 'Path segments should be valid' }
];

const SPECTRAL_RULES = [
  { code: 'contact-properties', name: 'Contact Properties', description: 'Contact object must have name, url, or email' },
  { code: 'duplicated-entry-in-enum', name: 'No Duplicate Enum', description: 'Enum values must not have duplicates' },
  { code: 'info-contact', name: 'Info Contact', description: 'Info object should have contact object' },
  { code: 'info-description', name: 'Info Description', description: 'Info object should have description' },
  { code: 'info-license', name: 'Info License', description: 'Info object should have license' },
  { code: 'license-url', name: 'License URL', description: 'License object should include url' },
  { code: 'no-$ref-siblings', name: 'No $ref Siblings', description: '$ref cannot be placed next to other properties' },
  { code: 'no-eval-in-markdown', name: 'No Eval in Markdown', description: 'Markdown descriptions must not have eval()' },
  { code: 'no-script-tags-in-markdown', name: 'No Script Tags', description: 'Markdown descriptions must not have script tags' },
  { code: 'openapi-tags', name: 'OpenAPI Tags', description: 'OpenAPI object should have non-empty tags array' },
  { code: 'openapi-tags-alphabetical', name: 'Alphabetical Tags', description: 'OpenAPI tags should be in alphabetical order' },
  { code: 'openapi-tags-uniqueness', name: 'Unique Tags', description: 'OpenAPI tags must have unique names' },
  { code: 'operation-description', name: 'Operation Description', description: 'Operation should have description' },
  { code: 'operation-operationId', name: 'Operation ID', description: 'Operation should have operationId' },
  { code: 'operation-operationId-unique', name: 'Unique Operation ID', description: 'Every operation must have unique operationId' },
  { code: 'operation-operationId-valid-in-url', name: 'Valid Operation ID', description: 'operationId should be URL-safe' },
  { code: 'operation-parameters', name: 'Operation Parameters', description: 'Operation parameters are unique and valid' },
  { code: 'operation-singular-tag', name: 'Single Tag', description: 'Operation should have only one tag' },
  { code: 'operation-success-response', name: 'Success Response', description: 'Operation must have at least one 2xx or 3xx response' },
  { code: 'operation-tags', name: 'Operation Tags', description: 'Operation should have non-empty tags array' },
  { code: 'operation-tag-defined', name: 'Tag Defined', description: 'Operation tags must be defined in global tags' },
  { code: 'path-declarations-must-exist', name: 'Path Must Exist', description: 'Path parameter declarations must exist' },
  { code: 'path-keys-no-trailing-slash', name: 'No Trailing Slash', description: 'Path must not end with slash' },
  { code: 'path-not-include-query', name: 'No Query in Path', description: 'Path must not include query string' },
  { code: 'path-params', name: 'Path Parameters', description: 'Path parameters must be defined and used' },
  { code: 'tag-description', name: 'Tag Description', description: 'Tag object should have description' },
  { code: 'component-description', name: 'Component Description', description: 'Components should have descriptions' },
  { code: 'oas3-api-servers', name: 'API Servers', description: 'OpenAPI should have servers array' },
  { code: 'oas3-examples-value-or-externalValue', name: 'Example Value', description: 'Examples must have value or externalValue' },
  { code: 'oas3-operation-security-defined', name: 'Security Defined', description: 'Operation security must be defined in components' },
  { code: 'oas3-parameter-description', name: 'Parameter Description', description: 'Parameter objects should have description' },
  { code: 'oas3-schema', name: 'Valid Schema', description: 'Object must be valid against OpenAPI 3.0 schema' },
  { code: 'oas3-server-not-example.com', name: 'No Example Server', description: 'Server URL should not be example.com' },
  { code: 'oas3-server-trailing-slash', name: 'Server Trailing Slash', description: 'Server URL should not have trailing slash' },
  { code: 'oas3-unused-component', name: 'Unused Component', description: 'Components should be referenced' },
  { code: 'oas3-valid-media-example', name: 'Valid Media Example', description: 'Examples must be valid against schema' },
  { code: 'oas3-valid-schema-example', name: 'Valid Schema Example', description: 'Schema examples must be valid' }
];

function RulesConfigModal({ darkMode, onClose, isAuthenticated, isAdmin, customRules, onCustomRulesChange }) {
  const [spectralEnabled, setSpectralEnabled] = useState(false);
  const [spectralRules, setSpectralRules] = useState({});
  const [ibmEnabled, setIbmEnabled] = useState(false);
  const [ibmRules, setIbmRules] = useState({});
  const [loading, setLoading] = useState(true);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [newRule, setNewRule] = useState({ name: '', description: '', code: '' });

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      if (isAuthenticated) {
        const config = await api.getRulesConfig();
        setSpectralEnabled(config.spectral_enabled === 1);
        setSpectralRules(config.spectral_rules || {});
        setIbmEnabled(config.ibm_enabled === 1);
        setIbmRules(config.ibm_rules || {});
        setIsAdminUser(config.is_admin === true);
        
        // Load custom rules from server
        const serverRules = config.custom_rules || [];
        const rulesWithFunctions = serverRules.map(rule => ({
          ...rule,
          validate: new Function('spec', rule.code)
        }));
        onCustomRulesChange(rulesWithFunctions);
      } else {
        const enabled = localStorage.getItem('spectralEnabled') !== 'false';
        const rules = JSON.parse(localStorage.getItem('spectralRules') || '{}');
        setSpectralEnabled(enabled);
        setSpectralRules(rules);
        setIsAdminUser(false);
      }
    } catch (err) {
      console.error('Failed to load rules:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSpectralToggle = async (enabled) => {
    setSpectralEnabled(enabled);
    if (isAuthenticated) {
      try {
        await api.updateRulesConfig({ spectral_enabled: enabled });
      } catch (err) {
        console.error('Failed to update spectral toggle:', err);
      }
    } else {
      localStorage.setItem('spectralEnabled', enabled);
    }
  };

  const handleIbmToggle = async (enabled) => {
    setIbmEnabled(enabled);
    if (isAuthenticated) {
      try {
        await api.updateRulesConfig({ ibm_enabled: enabled });
      } catch (err) {
        console.error('Failed to update IBM toggle:', err);
      }
    }
  };

  const handleRuleToggle = async (ruleCode, enabled, ruleType = 'spectral') => {
    if (ruleType === 'spectral') {
      const newRules = { ...spectralRules, [ruleCode]: enabled };
      setSpectralRules(newRules);
      
      if (isAuthenticated) {
        try {
          await api.updateRulesConfig({ spectral_rules: newRules });
        } catch (err) {
          console.error('Failed to update rule:', err);
        }
      } else {
        localStorage.setItem('spectralRules', JSON.stringify(newRules));
      }
    } else if (ruleType === 'ibm') {
      const newRules = { ...ibmRules, [ruleCode]: enabled };
      setIbmRules(newRules);
      
      if (isAuthenticated) {
        try {
          await api.updateRulesConfig({ ibm_rules: newRules });
        } catch (err) {
          console.error('Failed to update IBM rule:', err);
        }
      }
    }
  };

  const handleSave = () => {
    window.location.reload();
  };

  const toggleCustomRule = async (index) => {
    const updated = customRules.map((rule, i) => 
      i === index ? { ...rule, enabled: !rule.enabled } : rule
    );
    onCustomRulesChange(updated);
    
    if (isAuthenticated && isAdminUser) {
      const rulesToSave = updated.map(r => ({
        name: r.name,
        description: r.description,
        code: r.code,
        enabled: r.enabled
      }));
      await api.updateRulesConfig({ custom_rules: rulesToSave });
    }
  };

  const addCustomRule = async () => {
    try {
      const validate = new Function('spec', newRule.code);
      const rule = {
        ...newRule,
        enabled: true,
        validate
      };
      const updatedRules = [...customRules, rule];
      onCustomRulesChange(updatedRules);
      
      if (isAuthenticated && isAdminUser) {
        const rulesToSave = updatedRules.map(r => ({
          name: r.name,
          description: r.description,
          code: r.code,
          enabled: r.enabled
        }));
        await api.updateRulesConfig({ custom_rules: rulesToSave });
      }
      
      setNewRule({ name: '', description: '', code: '' });
      setShowForm(false);
    } catch (error) {
      alert('Invalid rule code: ' + error.message);
    }
  };

  const removeCustomRule = async (index) => {
    const updated = customRules.filter((_, i) => i !== index);
    onCustomRulesChange(updated);
    
    if (isAuthenticated && isAdminUser) {
      const rulesToSave = updated.map(r => ({
        name: r.name,
        description: r.description,
        code: r.code,
        enabled: r.enabled
      }));
      await api.updateRulesConfig({ custom_rules: rulesToSave });
    }
  };

  if (loading) {
    return (
      <Overlay onClick={onClose}>
        <Modal $darkMode={darkMode} onClick={e => e.stopPropagation()}>
          <Header $darkMode={darkMode}>
            <Title $darkMode={darkMode}>Loading...</Title>
          </Header>
        </Modal>
      </Overlay>
    );
  }

  return (
    <Overlay onClick={onClose}>
      <Modal $darkMode={darkMode} onClick={e => e.stopPropagation()}>
        <Header $darkMode={darkMode}>
          <Title $darkMode={darkMode}>Validation Rules</Title>
          <CloseButton $darkMode={darkMode} onClick={onClose}>×</CloseButton>
        </Header>
        
        <Content>
          <Section>
            <SectionTitle $darkMode={darkMode}>
              🔍 Spectral Validation
              {!isAuthenticated && <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#8b949e' }}>(Login required)</span>}
              {isAuthenticated && !isAdminUser && <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#8b949e' }}>(Admin only)</span>}
            </SectionTitle>
            <RuleItem $darkMode={darkMode}>
              <RuleInfo>
                <RuleName $darkMode={darkMode}>Enable Spectral</RuleName>
                <RuleDescription $darkMode={darkMode}>
                  {isAuthenticated ? (isAdminUser ? 'Comprehensive OpenAPI validation with 100+ rules (Application-wide)' : 'Comprehensive OpenAPI validation (Configured by admin)') : 'Login to enable server-side Spectral validation'}
                </RuleDescription>
              </RuleInfo>
              <Toggle $darkMode={darkMode}>
                <input
                  type="checkbox"
                  checked={spectralEnabled}
                  onChange={(e) => handleSpectralToggle(e.target.checked)}
                  disabled={!isAuthenticated || !isAdminUser}
                />
                <span />
              </Toggle>
            </RuleItem>
          </Section>

          {isAuthenticated && (
            <Section>
              <SectionTitle $darkMode={darkMode}>
                🔷 IBM OpenAPI Ruleset
                {!isAdminUser && <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#8b949e' }}>(Admin only)</span>}
              </SectionTitle>
              <RuleItem $darkMode={darkMode}>
                <RuleInfo>
                  <RuleName $darkMode={darkMode}>Enable IBM Rules</RuleName>
                  <RuleDescription $darkMode={darkMode}>
                    {isAdminUser ? 'IBM Cloud API standards and best practices (Application-wide)' : 'IBM Cloud API standards (Configured by admin)'}
                  </RuleDescription>
                </RuleInfo>
                <Toggle $darkMode={darkMode}>
                  <input
                    type="checkbox"
                    checked={ibmEnabled}
                    onChange={(e) => handleIbmToggle(e.target.checked)}
                    disabled={!isAdminUser}
                  />
                  <span />
                </Toggle>
              </RuleItem>
            </Section>
          )}

          {isAuthenticated && isAdminUser && ibmEnabled && (
            <Section>
              <SectionTitle $darkMode={darkMode}>IBM Rules</SectionTitle>
              {IBM_RULES.map(rule => (
                <RuleItem key={rule.code} $darkMode={darkMode}>
                  <RuleInfo>
                    <RuleName $darkMode={darkMode}>{rule.name}</RuleName>
                    <RuleDescription $darkMode={darkMode}>{rule.description}</RuleDescription>
                  </RuleInfo>
                  <Toggle $darkMode={darkMode}>
                    <input
                      type="checkbox"
                      checked={ibmRules[rule.code] !== false}
                      onChange={(e) => handleRuleToggle(rule.code, e.target.checked, 'ibm')}
                    />
                    <span />
                  </Toggle>
                </RuleItem>
              ))}
            </Section>
          )}

          {isAuthenticated && isAdminUser && spectralEnabled && (
            <Section>
              <SectionTitle $darkMode={darkMode}>Spectral Rules</SectionTitle>
              {SPECTRAL_RULES.map(rule => (
                <RuleItem key={rule.code} $darkMode={darkMode}>
                  <RuleInfo>
                    <RuleName $darkMode={darkMode}>{rule.name}</RuleName>
                    <RuleDescription $darkMode={darkMode}>{rule.description}</RuleDescription>
                  </RuleInfo>
                  <Toggle $darkMode={darkMode}>
                    <input
                      type="checkbox"
                      checked={spectralRules[rule.code] !== false}
                      onChange={(e) => handleRuleToggle(rule.code, e.target.checked)}
                    />
                    <span />
                  </Toggle>
                </RuleItem>
              ))}
            </Section>
          )}

          <Section>
            <SectionTitle $darkMode={darkMode}>📝 Custom Rules ({customRules.length})</SectionTitle>
            {customRules.map((rule, index) => (
              <RuleItem key={index} $darkMode={darkMode}>
                <RuleInfo>
                  <RuleName $darkMode={darkMode}>{rule.name}</RuleName>
                  <RuleDescription $darkMode={darkMode}>{rule.description}</RuleDescription>
                </RuleInfo>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Toggle $darkMode={darkMode}>
                    <input
                      type="checkbox"
                      checked={rule.enabled}
                      onChange={() => toggleCustomRule(index)}
                    />
                    <span />
                  </Toggle>
                  <button 
                    onClick={() => removeCustomRule(index)} 
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: darkMode ? '#8b949e' : '#586069',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px'
                    }}
                  >
                    ×
                  </button>
                </div>
              </RuleItem>
            ))}

            {showForm ? (
              <div style={{ 
                marginTop: '8px', 
                padding: '12px', 
                border: `1px solid ${darkMode ? '#30363d' : '#d1d5da'}`,
                borderRadius: '6px',
                background: darkMode ? '#161b22' : '#f6f8fa'
              }}>
                <input
                  placeholder="Rule name"
                  value={newRule.name}
                  onChange={(e) => setNewRule({...newRule, name: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '6px',
                    marginBottom: '8px',
                    border: `1px solid ${darkMode ? '#30363d' : '#d1d5da'}`,
                    borderRadius: '4px',
                    background: darkMode ? '#0d1117' : '#ffffff',
                    color: darkMode ? '#e6edf3' : '#24292f',
                    fontSize: '13px'
                  }}
                />
                <input
                  placeholder="Description"
                  value={newRule.description}
                  onChange={(e) => setNewRule({...newRule, description: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '6px',
                    marginBottom: '8px',
                    border: `1px solid ${darkMode ? '#30363d' : '#d1d5da'}`,
                    borderRadius: '4px',
                    background: darkMode ? '#0d1117' : '#ffffff',
                    color: darkMode ? '#e6edf3' : '#24292f',
                    fontSize: '13px'
                  }}
                />
                <textarea
                  placeholder="return spec.paths && Object.keys(spec.paths).length === 0 ? {message: 'No paths defined', type: 'warning'} : null;"
                  value={newRule.code}
                  onChange={(e) => setNewRule({...newRule, code: e.target.value})}
                  style={{
                    width: '100%',
                    height: '80px',
                    padding: '6px',
                    marginBottom: '8px',
                    border: `1px solid ${darkMode ? '#30363d' : '#d1d5da'}`,
                    borderRadius: '4px',
                    background: darkMode ? '#0d1117' : '#ffffff',
                    color: darkMode ? '#e6edf3' : '#24292f',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    resize: 'vertical'
                  }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button $darkMode={darkMode} $primary onClick={addCustomRule}>Add</Button>
                  <Button $darkMode={darkMode} onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowForm(true)}
                style={{
                  width: '100%',
                  padding: '10px',
                  marginTop: '8px',
                  border: `2px dashed ${darkMode ? '#30363d' : '#d1d5da'}`,
                  borderRadius: '6px',
                  background: darkMode ? '#0d1117' : '#ffffff',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#0969da',
                  transition: 'all 0.15s'
                }}
              >
                + Add Custom Rule
              </button>
            )}
          </Section>
        </Content>

        <Footer $darkMode={darkMode}>
          <Button $darkMode={darkMode} onClick={onClose}>{isAuthenticated && !isAdminUser ? 'Close' : 'Cancel'}</Button>
          {(isAdminUser || !isAuthenticated) && <Button $darkMode={darkMode} $primary onClick={handleSave}>Save & Reload</Button>}
        </Footer>
      </Modal>
    </Overlay>
  );
}

export default RulesConfigModal;
